import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Trash, CheckCircle, Circle, BellRinging, Alarm } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { FOLLOWUP_TYPES } from "@/lib/constants";
import { PageHeader, EmptyState, Stat } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/DatePicker";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { title: "", branch_id: "none", due_date: "", notes: "", assigned_to: "none", status: "pending", followup_type: "callback", linked_type: "none", linked_id: "", linked_label: "" };
const VIEWS = { today: "Today", upcoming: "Upcoming", overdue: "Overdue", all: "All Pending", completed: "Completed" };
const today = () => new Date().toISOString().slice(0, 10);

export default function Followups() {
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [team, setTeam] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [view, setView] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [assignee, setAssignee] = useState("all");
  const [snoozeFor, setSnoozeFor] = useState(null);
  const [snooze, setSnooze] = useState({ due_date: "", reason: "" });
  const [completeFor, setCompleteFor] = useState(null);
  const [resolution, setResolution] = useState("");

  const load = () => api.get("/followups").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
    api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
    api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {});
    api.get("/invoices").then((r) => setInvoices(r.data)).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (params.get("new")) { setForm({ ...EMPTY, due_date: today() }); setOpen(true); } }, [params]);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const branchName = (id) => { const b = branches.find((x) => x.id === id); return b ? `${b.bank_name} — ${b.branch_name}` : null; };
  const memberName = (id) => team.find((t) => t.id === id)?.name;

  const linkOptions = () => {
    if (form.linked_type === "job") return jobs.map((j) => ({ id: j.id, label: `${j.reference} · ${j.applicant_name}` }));
    if (form.linked_type === "invoice") return invoices.map((i) => ({ id: i.id, label: `${i.invoice_number} · ${i.bill_to_name}` }));
    if (form.linked_type === "client") return clients.map((c) => ({ id: c.id, label: c.name }));
    if (form.linked_type === "branch") return branches.map((b) => ({ id: b.id, label: `${b.bank_name} — ${b.branch_name}` }));
    return [];
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const opts = linkOptions();
      await api.post("/followups", {
        ...form,
        branch_id: form.branch_id === "none" ? null : form.branch_id,
        assigned_to: form.assigned_to === "none" ? null : form.assigned_to,
        linked_type: form.linked_type === "none" ? null : form.linked_type,
        linked_id: form.linked_id || null,
        linked_label: opts.find((o) => o.id === form.linked_id)?.label || "",
      });
      toast.success("Follow-up added");
      setOpen(false); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const reopen = async (f) => {
    try { await api.put(`/followups/${f.id}`, { ...f, branch_id: f.branch_id || null, assigned_to: f.assigned_to || null, status: "pending" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doComplete = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/followups/${completeFor.id}/complete`, { resolution_notes: resolution });
      toast.success("Marked done");
      setCompleteFor(null); setResolution(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doSnooze = async (e) => {
    e.preventDefault();
    if (!snooze.due_date) { toast.error("Pick a new date"); return; }
    try {
      await api.post(`/followups/${snoozeFor.id}/snooze`, snooze);
      toast.success("Snoozed");
      setSnoozeFor(null); setSnooze({ due_date: "", reason: "" }); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/followups/${id}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const t = today();
  const pending = items.filter((i) => i.status === "pending");
  const byView = {
    today: pending.filter((f) => (f.due_date || "").slice(0, 10) === t),
    upcoming: pending.filter((f) => (f.due_date || "").slice(0, 10) > t),
    overdue: pending.filter((f) => (f.due_date || "").slice(0, 10) < t),
    all: pending,
    completed: items.filter((i) => i.status === "done"),
  };
  const shown = byView[view].filter((f) => {
    const okType = typeFilter === "all" || (f.followup_type || "other") === typeFilter;
    const okWho = assignee === "all" || f.assigned_to === assignee;
    return okType && okWho;
  });

  const Row = ({ f }) => {
    const overdue = f.status === "pending" && f.due_date && f.due_date.slice(0, 10) < t;
    return (
      <div className="flex items-start gap-3 p-4 rounded-md border border-border bg-white transition-transform duration-200 hover:-translate-y-0.5 hover:shadow" data-testid={`followup-${f.id}`}>
        <button onClick={() => (f.status === "done" ? reopen(f) : (setCompleteFor(f), setResolution("")))} className="mt-0.5 shrink-0" data-testid={`toggle-followup-${f.id}`}>
          {f.status === "done" ? <CheckCircle size={22} weight="fill" className="text-emerald-500" /> : <Circle size={22} className="text-muted-foreground hover:text-accent transition-colors duration-150" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className={`font-medium ${f.status === "done" ? "line-through text-muted-foreground" : "text-primary"}`}>{f.title}</div>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5 text-xs">
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted border border-border text-muted-foreground">{FOLLOWUP_TYPES[f.followup_type] || "Other"}</span>
            {f.due_date && <span className={`font-mono ${overdue ? "text-destructive font-semibold" : "text-muted-foreground"}`}>Due {f.due_date.slice(0, 10)}{overdue ? " · Overdue" : ""}</span>}
            {f.snoozed && <span className="text-amber-600 inline-flex items-center gap-1"><Alarm size={12} /> snoozed</span>}
            {f.linked_label && <span className="text-muted-foreground">{f.linked_label}</span>}
            {branchName(f.branch_id) && <span className="text-muted-foreground">{branchName(f.branch_id)}</span>}
            {memberName(f.assigned_to) && <span className="text-accent">@{memberName(f.assigned_to)}</span>}
          </div>
          {f.notes && <div className="text-sm text-muted-foreground mt-1">{f.notes}</div>}
          {f.resolution_notes && <div className="text-sm text-emerald-700 mt-1">Resolved: {f.resolution_notes}</div>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {f.status === "pending" && (
            <Button size="icon" variant="ghost" className="h-8 w-8 text-amber-600" title="Snooze" onClick={() => { setSnoozeFor(f); setSnooze({ due_date: "", reason: "" }); }} data-testid={`snooze-followup-${f.id}`}>
              <Alarm size={16} />
            </Button>
          )}
          <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => remove(f.id)} data-testid={`delete-followup-${f.id}`}><Trash size={15} /></Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        overline="Task Tracker" title="Follow-ups & Reminders"
        subtitle={`${pending.length} pending · ${byView.overdue.length} overdue · ${byView.completed.length} done`}
        action={<Button onClick={() => { setForm({ ...EMPTY, due_date: today() }); setOpen(true); }} data-testid="add-followup-btn" className="rounded-full bg-primary hover:bg-[#113A64]"><Plus className="mr-1" size={16} /> Add Follow-up</Button>}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Due today" value={byView.today.length} tone="text-accent" testid="stat-fu-today" />
        <Stat label="Overdue" value={byView.overdue.length} tone={byView.overdue.length ? "text-rose-600" : "text-emerald-600"} testid="stat-fu-overdue" />
        <Stat label="Upcoming" value={byView.upcoming.length} testid="stat-fu-upcoming" />
        <Stat label="Completed" value={byView.completed.length} tone="text-emerald-600" testid="stat-fu-done" />
      </div>

      <div className="flex flex-wrap gap-2">
        {Object.entries(VIEWS).map(([k, label]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`fu-view-${k}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors duration-150 ${view === k ? "bg-primary text-white border-primary" : "bg-white text-muted-foreground border-border hover:border-accent"}`}>
            {label} ({byView[k].length})
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-3">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[200px]" data-testid="fu-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {Object.entries(FOLLOWUP_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={assignee} onValueChange={setAssignee}>
          <SelectTrigger className="w-[200px]" data-testid="fu-assignee-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Anyone</SelectItem>
            {team.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        {shown.length === 0 && <EmptyState icon={BellRinging}>Nothing here. {view === "overdue" ? "Nothing is overdue — nicely done." : "Add a follow-up to get started."}</EmptyState>}
        {shown.map((f) => <Row key={f.id} f={f} />)}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">Add Follow-up</DialogTitle><DialogDescription>Link it to a job, client, invoice or branch so context is one click away.</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-3" data-testid="followup-form">
            <div className="space-y-1.5"><Label>Title *</Label><Input required value={form.title} onChange={set("title")} placeholder="Call SBI Pandri manager" data-testid="followup-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Category</Label>
                <Select value={form.followup_type} onValueChange={set("followup_type")}>
                  <SelectTrigger data-testid="followup-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(FOLLOWUP_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Due Date *</Label><DatePicker value={form.due_date} onChange={set("due_date")} placeholder="Select date" clearable className="w-full" data-testid="followup-due" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Link to</Label>
                <Select value={form.linked_type} onValueChange={(v) => setForm({ ...form, linked_type: v, linked_id: "" })}>
                  <SelectTrigger data-testid="followup-link-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nothing</SelectItem>
                    <SelectItem value="job">Job</SelectItem>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="invoice">Invoice</SelectItem>
                    <SelectItem value="branch">Bank Branch</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.linked_type !== "none" && (
                <div className="space-y-1.5"><Label>Record</Label>
                  <Select value={form.linked_id} onValueChange={set("linked_id")}>
                    <SelectTrigger data-testid="followup-link-id"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{linkOptions().map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="space-y-1.5"><Label>Related Branch</Label>
              <Select value={form.branch_id} onValueChange={set("branch_id")}><SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent><SelectItem value="none">None</SelectItem>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.bank_name} — {b.branch_name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5"><Label>Assign To</Label>
              <Select value={form.assigned_to} onValueChange={set("assigned_to")}><SelectTrigger><SelectValue placeholder="Anyone" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Anyone</SelectItem>{team.map((tm) => <SelectItem key={tm.id} value={tm.id}>{tm.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea value={form.notes} onChange={set("notes")} rows={2} /></div>
            <DialogFooter><Button type="submit" className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="save-followup-btn">Add</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!snoozeFor} onOpenChange={(o) => !o && setSnoozeFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-2xl">Snooze Follow-up</DialogTitle><DialogDescription>{snoozeFor?.title}</DialogDescription></DialogHeader>
          <form onSubmit={doSnooze} className="space-y-3" data-testid="snooze-form">
            <div className="space-y-1.5"><Label>New Due Date *</Label><DatePicker value={snooze.due_date} onChange={(v) => setSnooze({ ...snooze, due_date: v })} placeholder="Select date" clearable className="w-full" data-testid="snooze-due" /></div>
            <div className="space-y-1.5"><Label>Reason</Label><Input value={snooze.reason} onChange={(e) => setSnooze({ ...snooze, reason: e.target.value })} placeholder="Why is it being pushed?" data-testid="snooze-reason" /></div>
            <DialogFooter><Button type="submit" className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="save-snooze-btn">Snooze</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!completeFor} onOpenChange={(o) => !o && setCompleteFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-2xl">Mark Done</DialogTitle><DialogDescription>{completeFor?.title}</DialogDescription></DialogHeader>
          <form onSubmit={doComplete} className="space-y-3" data-testid="complete-form">
            <div className="space-y-1.5"><Label>Resolution notes (optional)</Label><Textarea rows={3} value={resolution} onChange={(e) => setResolution(e.target.value)} placeholder="What was the outcome?" data-testid="resolution-notes" /></div>
            <DialogFooter><Button type="submit" className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-complete-btn">Mark Done</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
